import { ReactNode } from 'react'
import { Card } from '@/components/ui'
import { LucideIcon } from 'lucide-react'

export interface MetricCardProps {
  /** Иконка метрики */
  icon?: LucideIcon
  /** Цвет иконки (Tailwind класс) */
  iconColor?: string
  /** Заголовок карточки */
  title: string
  /** Основное значение */
  value: string | number
  /** Цвет значения (Tailwind класс) */
  valueColor?: string
  /** Дополнительная информация под значением */
  subtitle?: string | ReactNode
  /** Цвет subtitle */
  subtitleColor?: string
  /** Значок (бейдж) справа от заголовка */
  badge?: ReactNode
  /** Дополнительный контент в карточке */
  children?: ReactNode
}

/**
 * Универсальный компонент карточки метрики
 * Используется для отображения ключевых показателей на странице аналитики
 */
export function MetricCard({
  icon: Icon,
  iconColor = 'text-primary',
  title,
  value,
  valueColor = 'text-foreground',
  subtitle,
  subtitleColor = 'text-muted-foreground',
  badge,
  children
}: MetricCardProps) {
  return (
    <Card>
      {/* Заголовок с иконкой и бейджем */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-5 h-5 ${iconColor}`} />}
          <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
        </div>
        {badge}
      </div>

      {/* Основное значение */}
      <div className="flex items-center justify-between mb-1">
        <p className={`text-2xl font-bold ${valueColor}`}>
          {value}
        </p>
      </div>

      {/* Подзаголовок */}
      {subtitle && (
        <div className={`text-sm ${subtitleColor}`}>
          {subtitle}
        </div>
      )}

      {/* Дополнительный контент */}
      {children}
    </Card>
  )
}

/**
 * Компонент бейджа отклонения от плана
 * Показывает процент отклонения с цветовой индикацией
 */
export function PlanDeviationBadge({ 
  factValue, 
  planValue 
}: { 
  factValue: number; 
  planValue: number 
}) {
  if (!planValue || planValue === 0) return null
  
  const deviationPercent = ((factValue - planValue) / planValue * 100).toFixed(0)
  const isPositive = factValue >= planValue
  
  return (
    <span className={`px-3 py-1 rounded-full text-base font-bold ${
      isPositive
        ? 'bg-green-100 text-green-700' 
        : 'bg-red-100 text-red-700'
    }`}>
      {deviationPercent}%
    </span>
  )
}
